import envConfig from '@hulu/env-config';

import { RoutePath } from '../../../constants/routePaths';
import { ANALYTICS_PROPERTY_KEYS, AUTH_STATES, PRODUCT_ENVIRONMENTS, PRODUCT_IDS } from '../constants';
import type { AnalyticsEventProperties } from '../types';

export type AnalyticsSessionContext = {
  adAccountId?: string;
  organizationId?: string;
  organizationName?: string;
  campaignId?: string;
  lineItemId?: string;
  authState?: (typeof AUTH_STATES)[keyof typeof AUTH_STATES];
};

let sessionContext: AnalyticsSessionContext = {};
let selectedAdAccountId: string | undefined;
let lastSyncedPathname = '';

const ROUTE_PARAM_PATTERNS = {
  campaignId: /\/campaigns\/([^/]+)/,
  lineItemId: /\/lineItem\/([^/]+)/,
  lineItemsSummaryId: /\/lineItems\/([^/]+)/,
} as const;

const RESERVED_CAMPAIGN_PATH_SEGMENTS = new Set(['create', 'lineItems']);

const RESERVED_REPORTS_PATH_SEGMENTS = new Set(['mixed', 'overview', 'summary']);

const RESERVED_TOP_LEVEL_PATH_SEGMENTS = new Set(
  Object.values(RoutePath)
    .map((routePath) => routePath.replace(/^\//, ''))
    .filter(Boolean)
);

const AD_ACCOUNT_ID_PATH_PATTERNS = [
  /\/admin\/organizations\/[^/]+\/ad-accounts\/([^/]+)/,
  /\/account\/brands\/adAccount\/([^/]+)/,
  /\/reports\/mixed\/self-serve\/([^/]+)/,
  /^\/([^/]+)\/reports(?:\/|$)/,
  /^\/([^/]+)\/campaigns(?:\/|$)/,
] as const;

const extractReportsAdAccountIdFromPathname = (pathname: string): string | undefined => {
  const reportsMatch = /^\/reports\/([^/]+)(?:\/|$)/.exec(pathname);
  const segment = reportsMatch?.[1];

  if (!segment || RESERVED_REPORTS_PATH_SEGMENTS.has(segment)) {
    return undefined;
  }

  return segment;
};

const ORGANIZATION_ID_PATH_PATTERNS = [/\/admin\/organizations\/([^/]+)/, /^\/([^/]+)\/report-template\//] as const;

const isCampaignListPath = (pathname: string): boolean => /\/campaigns\/?$/.test(pathname);

const isCampaignCreatePath = (pathname: string): boolean => pathname.includes(`${RoutePath.CAMPAIGNS}/create`);

const isCampaignAreaPath = (pathname: string): boolean => pathname.includes(RoutePath.CAMPAIGNS);

const isLineItemScopedPath = (pathname: string): boolean =>
  ROUTE_PARAM_PATTERNS.lineItemId.test(pathname) || ROUTE_PARAM_PATTERNS.lineItemsSummaryId.test(pathname);

export const extractCampaignIdFromPathname = (pathname: string): string | undefined => {
  const campaignMatch = ROUTE_PARAM_PATTERNS.campaignId.exec(pathname);
  const campaignId = campaignMatch?.[1];

  if (!campaignId || RESERVED_CAMPAIGN_PATH_SEGMENTS.has(campaignId)) {
    return undefined;
  }

  return campaignId;
};

export const extractLineItemIdFromPathname = (pathname: string): string | undefined => {
  const builderLineItemMatch = ROUTE_PARAM_PATTERNS.lineItemId.exec(pathname);
  if (builderLineItemMatch?.[1]) {
    return builderLineItemMatch[1];
  }

  const summaryLineItemMatch = ROUTE_PARAM_PATTERNS.lineItemsSummaryId.exec(pathname);
  return summaryLineItemMatch?.[1];
};

export const extractAdAccountIdFromPathname = (pathname: string): string | undefined => {
  const reportsAdAccountId = extractReportsAdAccountIdFromPathname(pathname);
  if (reportsAdAccountId) {
    return reportsAdAccountId;
  }

  for (const pattern of AD_ACCOUNT_ID_PATH_PATTERNS) {
    const match = pattern.exec(pathname);
    const candidate = match?.[1];

    if (candidate && !RESERVED_TOP_LEVEL_PATH_SEGMENTS.has(candidate)) {
      return candidate;
    }
  }

  const rootAdAccountMatch = /^\/([^/]+)\/?$/.exec(pathname);
  const rootAdAccountId = rootAdAccountMatch?.[1];

  if (rootAdAccountId && !RESERVED_TOP_LEVEL_PATH_SEGMENTS.has(rootAdAccountId)) {
    return rootAdAccountId;
  }

  const firstSegmentMatch = /^\/([^/]+)\//.exec(pathname);
  const firstSegment = firstSegmentMatch?.[1];

  if (!firstSegment || RESERVED_TOP_LEVEL_PATH_SEGMENTS.has(firstSegment)) {
    return undefined;
  }

  return firstSegment;
};

export const extractOrganizationIdFromPathname = (pathname: string): string | undefined => {
  for (const pattern of ORGANIZATION_ID_PATH_PATTERNS) {
    const match = pattern.exec(pathname);
    const candidate = match?.[1];

    if (candidate && !RESERVED_TOP_LEVEL_PATH_SEGMENTS.has(candidate)) {
      return candidate;
    }
  }

  return undefined;
};

const resolveAdAccountId = (pathname: string): string | undefined =>
  extractAdAccountIdFromPathname(pathname) ?? selectedAdAccountId;

const resolveOrganizationId = (pathname: string, previous?: string): string | undefined =>
  extractOrganizationIdFromPathname(pathname) ?? previous;

const resolveCampaignId = (pathname: string, previous?: string): string | undefined => {
  const fromPath = extractCampaignIdFromPathname(pathname);
  if (fromPath) {
    return fromPath;
  }

  if (!isCampaignAreaPath(pathname) || isCampaignListPath(pathname) || isCampaignCreatePath(pathname)) {
    return undefined;
  }

  if (isLineItemScopedPath(pathname)) {
    return previous;
  }

  return undefined;
};

const resolveLineItemId = (pathname: string, previous?: string): string | undefined => {
  const fromPath = extractLineItemIdFromPathname(pathname);
  if (fromPath) {
    return fromPath;
  }

  if (!isLineItemScopedPath(pathname)) {
    return undefined;
  }

  return previous;
};

export const setAnalyticsSelectedAdAccountId = (adAccountId: string | undefined): void => {
  selectedAdAccountId = adAccountId;
  sessionContext = {
    ...sessionContext,
    adAccountId: resolveAdAccountId(lastSyncedPathname),
  };
};

export const syncAnalyticsContextFromPathname = (pathname: string): void => {
  lastSyncedPathname = pathname;

  sessionContext = {
    ...sessionContext,
    adAccountId: resolveAdAccountId(pathname),
    organizationId: resolveOrganizationId(pathname, sessionContext.organizationId),
    campaignId: resolveCampaignId(pathname, sessionContext.campaignId),
    lineItemId: resolveLineItemId(pathname, sessionContext.lineItemId),
  };
};

export const setAnalyticsSessionContext = (context: Partial<AnalyticsSessionContext>): void => {
  sessionContext = {
    ...sessionContext,
    ...context,
  };
};

export const clearAnalyticsSessionContext = (): void => {
  sessionContext = {};
  selectedAdAccountId = undefined;
  lastSyncedPathname = '';
};

export const getAnalyticsSessionContextProperties = (): AnalyticsEventProperties => ({
  ...(sessionContext.adAccountId ? { [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: sessionContext.adAccountId } : {}),
  ...(sessionContext.organizationId
    ? { [ANALYTICS_PROPERTY_KEYS.ORGANIZATION_ID]: sessionContext.organizationId }
    : {}),
  ...(sessionContext.organizationName
    ? { [ANALYTICS_PROPERTY_KEYS.ORGANIZATION_NAME]: sessionContext.organizationName }
    : {}),
  ...(sessionContext.campaignId ? { [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: sessionContext.campaignId } : {}),
  ...(sessionContext.lineItemId ? { [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: sessionContext.lineItemId } : {}),
  [ANALYTICS_PROPERTY_KEYS.PRODUCT_ID]: PRODUCT_IDS.DCM,
  [ANALYTICS_PROPERTY_KEYS.PRODUCT_ENVIRONMENT]:
    envConfig.NODE_ENV === 'production' ? PRODUCT_ENVIRONMENTS.PRODUCTION : PRODUCT_ENVIRONMENTS.NON_PRODUCTION,
  ...(sessionContext.authState ? { [ANALYTICS_PROPERTY_KEYS.AUTH_STATE]: sessionContext.authState } : {}),
});
